import React from "react";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";
import Typography from "@mui/material/Typography";
import CleaningSettingsForm from "./CleaningSettingsForm";
import CleaningZone from "../../../../interfaces/robot/rooms/cleaning-zone";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../../store/SharedProvider";
import { selectSelectedRoom } from "../../../../store/room/room-selectors";
import TeachingCommandMessage, {
  TeachingCommand,
} from "../../../../interfaces/robot/rooms/hub-messages/teaching-command-message";
import { getPolygonAreaString } from "../../../../utils/map/zone-helper";
import ReadOnlyProtectedButton from "../../../Inputs/ReadOnlyProtectedButton";
import TeachAndRepeatZoneGeneratingPathState from "./TeachAndRepeatZoneGeneratingPathState";
import TeachAndRepeatSettingsForm from "./TeachAndRepeatSettingsForm";
import MessageType from "../../../../enums/robot/rsw/message-types";
import { selectIsRobotConnected } from "../../../../store/robot-configurations/robot-configurations-selectors";
import useDistanceUnitValue from "../../../../hooks/useDistanceUnitValue";
import { DistanceValueUnit } from "../../../../enums/configuration/units";
import { selectRobotRswSoftwareVersion } from "../../../../store/robot-configurations/robot-configurations-selectors";
import { isVersionAGreaterThanB } from "../../../../utils/version-util";
import { selectIsWebApp } from "../../../../store/app-configurations/app-configurations-selectors";
import Stack from "@mui/material/Stack";

const SelectedZoneFormTeachAndRepeatZoneDetails: React.FC<{
  zone: CleaningZone;
}> = ({ zone }) => {
  const dispatch = useSharedDispatch();

  const { sendRobotWebsocketMessage, setIsGeneratingTeachPath } = useSharedActions();

  const { multiplier, label: distanceUnitLabel } = useDistanceUnitValue(0, DistanceValueUnit.METERS_SQUARED);

  const roomData = useSharedSelector((state) => selectSelectedRoom(state));
  const isRobotConnected = useSharedSelector((state) => selectIsRobotConnected(state));

  const rswSoftwareVersion = useSharedSelector((state) => selectRobotRswSoftwareVersion(state));

  const isWebapp = useSharedSelector((state) => selectIsWebApp(state));

  const { t } = useTranslation();

  const generatePathClickHandler = () => {
    if (roomData == null || zone.startPosition == null) {
      return;
    }

    const zonePoints = zone.points.map((p) => [p.point.coordinateX, p.point.coordinateY, p.point.coordinateTheta ?? 0]);

    const generatePathMsg: TeachingCommandMessage = {
      roomId: roomData.id,
      zoneId: zone.id,
      zoneName: zone.name,
      zonePoints: zonePoints,
      zoneStartPosition: [zone.startPosition.coordinateX, zone.startPosition.coordinateY, 0],
      type: TeachingCommand.AutoGeneratePath,
    };
    dispatch(setIsGeneratingTeachPath());
    dispatch(sendRobotWebsocketMessage({ message: generatePathMsg, type: MessageType.Teaching }));
  };

  return (
    <React.Fragment>
      <Grid item xs={12}>
        <Typography>
          {`${t("zone-area")}: ${getPolygonAreaString(
            zone.points.map((p) => p.point),
            multiplier,
          )}${distanceUnitLabel}`}
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <ReadOnlyProtectedButton
          variant="contained"
          fullWidth
          onClick={generatePathClickHandler}
          disabled={!isRobotConnected}
        >
          {t("teachingwizard-generate-path")}
        </ReadOnlyProtectedButton>
      </Grid>

      <Grid item xs={12}>
        <Stack spacing={2}>
          <CleaningSettingsForm zone={zone} />

          {(isWebapp || (rswSoftwareVersion && !isVersionAGreaterThanB("2026.1.0", rswSoftwareVersion))) && (
            <TeachAndRepeatSettingsForm zone={zone} />
          )}
        </Stack>
      </Grid>

      <TeachAndRepeatZoneGeneratingPathState />
    </React.Fragment>
  );
};

export default SelectedZoneFormTeachAndRepeatZoneDetails;
